import {
  PROJECT_TABS,
  UNDERLINE_NAV_ACTIVE,
  UNDERLINE_NAV_CANVAS,
  UNDERLINE_NAV_FG,
  UNDERLINE_NAV_HAIRLINE,
  UNDERLINE_NAV_MUTED,
  projectTabHighlight,
  projectTabHref,
  type ProjectTabId,
} from "@/lib/underline-nav";
import { cn } from "@/lib/utils";
import { Bot, CircleDot, Code, GitBranch, Settings } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CSSProperties } from "react";
import { NavLink, useLocation } from "react-router-dom";

const TAB_ICONS: Record<ProjectTabId, LucideIcon> = {
  files: Code,
  issues: CircleDot,
  changes: GitBranch,
  agents: Bot,
  settings: Settings,
};

export function ProjectUnderlineNav({
  projectBase,
  issueCount,
  changeCount,
}: {
  projectBase: string;
  issueCount?: number;
  changeCount?: number;
}) {
  const location = useLocation();
  const active = projectTabHighlight(location.pathname);

  return (
    <nav
      data-testid="project-underline-nav"
      aria-label="Project"
      className="underline-nav flex h-12 shrink-0 items-stretch gap-1 overflow-x-auto border-b px-3 sm:px-4"
      style={
        {
          borderBottomColor: UNDERLINE_NAV_HAIRLINE,
          background: UNDERLINE_NAV_CANVAS,
          ["--un-muted"]: UNDERLINE_NAV_MUTED,
          ["--un-fg"]: UNDERLINE_NAV_FG,
        } as CSSProperties
      }
    >
      {PROJECT_TABS.map((tab) => {
        const on = active === tab.id;
        const Icon = TAB_ICONS[tab.id];
        const counter = tab.id === "issues" ? issueCount : tab.id === "changes" ? changeCount : undefined;
        return (
          <NavLink
            key={tab.id}
            to={projectTabHref(projectBase, tab.to)}
            aria-current={on ? "page" : undefined}
            data-testid={`project-tab-${tab.id}`}
            data-mission-id={`tab:${tab.id}`}
            data-mission-route={tab.to}
            data-mission-panel={tab.id}
            data-active={on ? "true" : "false"}
            className={cn(
              "relative flex shrink-0 items-center gap-2 px-3 text-[14px] font-medium no-underline",
              "text-[color:var(--un-muted)] hover:text-[color:var(--un-fg)]",
              on && "text-[color:var(--un-fg)]",
            )}
          >
            <Icon className="size-4 shrink-0" aria-hidden strokeWidth={1.75} />
            <span>{tab.label}</span>
            {typeof counter === "number" && (
              <span
                className="rounded-full bg-white/10 px-1.5 py-px font-mono text-[11px] leading-4"
                style={{ color: UNDERLINE_NAV_MUTED }}
                data-testid={`project-tab-${tab.id}-count`}
              >
                {counter}
              </span>
            )}
            {on ? (
              <span
                aria-hidden
                className="absolute inset-x-2 bottom-[-1px] h-0.5 rounded-t-sm"
                style={{ background: UNDERLINE_NAV_ACTIVE }}
              />
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}
